import { Card } from '@payaca/ambrosia';
import { FC, useCallback, useMemo, type JSX } from 'react';

import BasicField from '@payaca/components/basicField/BasicField';
import SwitchField from '@payaca/components/switchField/SwitchField';

import useUpdateAccountAccreditations from '@/api/mutations/account/useUpdateAccountAccreditations';
import useGetMyAccountAccreditations from '@/api/queries/me/useGetMyAccountAccreditations';
import {
  useBusinessAccountForm,
  useUpdateBusinessAccount,
} from './useBusinessAccountForm';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';
import SettingsCardPlaceholder from './SettingsCardPlaceholder';
import SettingsSaveButton from './SettingsSaveButton';
import { AccountRegions } from '@payaca/types/accountTypes';

type FormState = {
  // Initialised from the account's string value; the numeric input writes
  // back number | null.
  gasSafeRegNo: string | number | null;
  niceicRegNo: string | null;
  mcsCertificationNumber: string | null;
  mcsBatteryStorageCertified: boolean;
};

/** True when the account is in a region with professional accreditation bodies. */
export const useHasAccreditations = (): boolean => {
  const region = useRegion();
  return useMemo(() => [AccountRegions.UK].includes(region), [region]);
};

type Props = {
  readOnly: boolean;
};

const CompanySettingsAccreditations: FC<Props> = ({
  readOnly,
}: Props): JSX.Element | null => {
  const hasAccreditations = useHasAccreditations();
  const account = useAccount();
  const { accreditations, isError } = useGetMyAccountAccreditations();

  if (!hasAccreditations) return null;
  if (!accreditations) {
    return <SettingsCardPlaceholder isError={isError} />;
  }

  return (
    <AccreditationsForm
      key={JSON.stringify([
        accreditations,
        account.gasSafeRegNo,
        account.niceicRegNo,
      ])}
      readOnly={readOnly}
      mcsCertificationNumber={accreditations.mcsCertificationNumber ?? null}
      mcsBatteryStorageCertified={accreditations.mcsBatteryStorageCertified}
    />
  );
};

const AccreditationsForm: FC<
  Props &
    Pick<FormState, 'mcsCertificationNumber' | 'mcsBatteryStorageCertified'>
> = ({ readOnly, mcsCertificationNumber, mcsBatteryStorageCertified }) => {
  const account = useAccount();
  const updateBusinessAccount = useUpdateBusinessAccount();
  const { mutateAsync: updateAccreditations } =
    useUpdateAccountAccreditations();

  const initialState: FormState = {
    gasSafeRegNo: account.gasSafeRegNo ?? null,
    niceicRegNo: account.niceicRegNo ?? null,
    mcsCertificationNumber,
    mcsBatteryStorageCertified,
  };

  const save = useCallback(
    async ({ gasSafeRegNo, niceicRegNo, ...mcs }: FormState) => {
      // The validated MCS write goes first so a rejected number saves nothing.
      await updateAccreditations(mcs);
      await updateBusinessAccount({ gasSafeRegNo, niceicRegNo });
    },
    [updateBusinessAccount, updateAccreditations]
  );

  const {
    formState,
    updateFormFields,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, {}, save);

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-2">
        <p>
          If your business is registered with any professional accreditation
          bodies, please enter your registration numbers below. These will
          automatically be entered into any forms you complete using Payaca.
        </p>
        <BasicField
          label={'Gas Safe reg. number'}
          name="gasSafeRegNo"
          value={formState.gasSafeRegNo}
          type="number"
          onChange={(value: { [gasSafeRegNo: string]: string }) => {
            if (Number(value.gasSafeRegNo) && value.gasSafeRegNo !== '') {
              updateFormFields({
                gasSafeRegNo: Number(value.gasSafeRegNo),
              });
            } else {
              updateFormFields({
                gasSafeRegNo: null,
              });
            }
          }}
          isDisabled={readOnly}
        />
        <BasicField
          label={'NICEIC reg. number'}
          name="niceicRegNo"
          value={formState.niceicRegNo}
          type="text"
          onChange={(value: { [niceicRegNo: string]: string }) => {
            if (value.niceicRegNo) {
              updateFormFields({
                niceicRegNo: value.niceicRegNo,
              });
            } else {
              updateFormFields({
                niceicRegNo: null,
              });
            }
          }}
          isDisabled={readOnly}
        />
        <BasicField
          label={'MCS certification number'}
          name="mcsCertificationNumber"
          value={formState.mcsCertificationNumber}
          type="text"
          onChange={(value: { [mcsCertificationNumber: string]: string }) => {
            updateFormFields({
              mcsCertificationNumber: value.mcsCertificationNumber || null,
            });
          }}
          isDisabled={readOnly}
        />
        <SwitchField
          name="mcsBatteryStorageCertified"
          value={formState.mcsBatteryStorageCertified}
          onChange={(value: {
            [mcsBatteryStorageCertified: string]: boolean;
          }) => {
            updateFormFields(value);
          }}
          label="MCS certified for battery storage (MIS 3012)"
          isDisabled={readOnly}
        />
        {!readOnly && (
          <SettingsSaveButton
            isDirty={isDirty}
            isSaving={isSaving}
            isValid={isValid}
            onSave={saveAndFlush}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsAccreditations;
